//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { getSocialNetworkFromPostType } from '@apis/universal-posts/universalPostsUtils';
import {
  FACEBOOK_MAX_IMAGE_FILE_SIZE_BYTES,
  FACEBOOK_MAX_VIDEO_DURATION_MILLIS,
} from '@facebook/constants/facebookConstants';
import {
  REELS_MAX_VIDEO_DURATION_MILLIS,
  REELS_MAX_VIDEO_SIZE,
  REELS_MIN_VIDEO_DURATION_MILLIS,
  STORY_MAX_IMAGE_SIZE,
  STORY_MAX_VIDEO_DURATION_MILLIS,
  STORY_MAX_VIDEO_SIZE,
  STORY_MIN_VIDEO_DURATION_MILLIS,
} from '@instagram-account/post-details/postDetailsUIConstants';
import { isValidIgReelsAspectRatio } from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsUtils';
import { MAX_IG_GALLERY_POST_MEDIA_COUNT } from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import {
  INSTAGRAM_FEED_MAX_IMAGE_SIZE_BYTES,
  INSTAGRAM_FEED_MAX_VIDEO_DURATION_MILLIS,
  INSTAGRAM_FEED_MAX_VIDEO_SIZE_BYTES,
  INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS,
  isValidIgImageAspectRatio,
  isValidIgVideoAspectRatio,
} from '@instagram-account/redux/post-details/instagramPostDetailsUtils';
import Cuid from '@models/Cuid';
import {
  IMlsMediaItem,
  getMediaFileSize,
  getMediaMetadata,
} from '@models/IMlsMediaItem';
import {
  isDefinedWithNetworkItem,
  isGif,
  isImage,
  isVideo,
} from '@models/INetworkItem';
import { SocialNetwork } from '@models/SocialNetwork';
import {
  MAX_CAROUSEL_PIN_IMAGES,
  PinterestPostDetailsValidationError,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import { validatePinMediaSpecs } from '@pinterest/redux/post-details/pinterestPostDetailsUtils';
import {
  TIKTOK_MAX_VIDEO_DURATION_MILLIS,
  TIKTOK_MIN_DIMENSION_PX,
  TIKTOK_MIN_VIDEO_DURATION_MILLIS,
} from '@tiktok/redux/schedule/post-details/video-automatic/tiktokVideoAutomaticPostDetailsModel';
import {
  MAX_TWITTER_GALLERY_IMAGE_COUNT,
  isTwitterMediaTooLarge,
  isTwitterMediaTooSmall,
} from '@twitter/redux/post-details/twitterPostDetailsModel';
import { getMediaAspectRatio } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import {
  YOUTUBE_MAX_VIDEO_DURATION_MILLIS,
  YOUTUBE_MAX_VIDEO_SIZE_BYTES,
} from '@universal-scheduling/components/scheduler/top-level-fields/shared-media/schedulerSharedMediaValidationUIConstants';
import { isDefined } from '@utils/filterPredicates';

export interface SchedulerMediaValidationError {
  cuids: Cuid[];
  postType: UniversalPostType;
  error: SchedulerMediaError;
}

export enum SchedulerMediaError {
  InvalidImageAspectRatio = 'InvalidImageAspectRatio',
  InvalidVideoAspectRatio = 'InvalidVideoAspectRatio',
  ImageFileTooLarge = 'ImageFileTooLarge',
  ImageFileTooSmall = 'ImageFileTooSmall',
  InvalidMediaType = 'InvalidMediaType',
  InvalidMixedMediaTypes = 'InvalidMixedMediaTypes',
  TooFewMediaItems = 'TooFewMediaItems',
  TooManyMediaItems = 'TooManyMediaItems',
  MediaDimensionsTooLarge = 'MediaDimensionsTooLarge',
  MediaDimensionsTooSmall = 'MediaDimensionsTooSmall',
  VideoFileTooLarge = 'VideoFileTooLarge',
  VideoFileTooSmall = 'VideoFileTooSmall',
  VideoTooLong = 'VideoTooLong',
  VideoTooShort = 'VideoTooShort',
}

type SchedulerMediaValidationErrorsByNetwork = Record<
  SocialNetwork,
  SchedulerMediaValidationError[]
>;

export const mediaTypeAndCountErrors = new Set([
  SchedulerMediaError.InvalidMediaType,
  SchedulerMediaError.InvalidMixedMediaTypes,
  SchedulerMediaError.TooManyMediaItems,
]);

export const mediaFormattingErrors = new Set([
  SchedulerMediaError.InvalidImageAspectRatio,
  SchedulerMediaError.InvalidVideoAspectRatio,
  SchedulerMediaError.ImageFileTooLarge,
  SchedulerMediaError.ImageFileTooSmall,
  SchedulerMediaError.VideoFileTooLarge,
  SchedulerMediaError.VideoFileTooSmall,
  SchedulerMediaError.MediaDimensionsTooLarge,
  SchedulerMediaError.MediaDimensionsTooSmall,
  SchedulerMediaError.VideoTooLong,
  SchedulerMediaError.VideoTooShort,
]);

export const validateSchedulerMedia = (
  mediaItems: IMlsMediaItem[],
  postTypes: UniversalPostType[],
): SchedulerMediaValidationError[] => {
  return postTypes.flatMap((postType) => {
    switch (postType) {
      case UniversalPostType.FacebookPageFeedPost:
        return validateFacebookPageFeedPostMedia(mediaItems);
      case UniversalPostType.TikTokVideoAutomaticPost:
        return validateTikTokVideoAutomaticPostMedia(mediaItems);
      case UniversalPostType.PinterestOrganicPin:
        return validatePinterestOrganicPinMedia(mediaItems);
      case UniversalPostType.TwitterMediaPost:
        return validateTwitterMediaPostMedia(mediaItems);
      case UniversalPostType.InstagramFeedAutomaticPost:
        return validateInstagramFeedAutomaticPostMedia(mediaItems);
      case UniversalPostType.InstagramFeedManualPost:
        return validateInstagramFeedManualPostMedia(mediaItems);
      case UniversalPostType.InstagramReelsAutomaticPost:
        return validateInstagramReelsAutomaticPostMedia(mediaItems);
      case UniversalPostType.InstagramReelsManualPost:
        return validateInstagramReelsManualPostMedia(mediaItems);
      case UniversalPostType.InstagramStoryGroupAutomaticPost:
        return validateInstagramStoryGroupAutomaticPostMedia(mediaItems);
      case UniversalPostType.InstagramStoryGroupPost:
        return validateInstagramStoryGroupPostMedia(mediaItems);
      case UniversalPostType.YouTubeAutomaticPost:
        return validateYouTubeAutomaticPostMedia(mediaItems);
    }
  });
};

export const getTooFewMediaErrors = (
  errors: SchedulerMediaValidationError[],
): SchedulerMediaValidationError[] => {
  return errors.filter(
    ({ error }) => error === SchedulerMediaError.TooFewMediaItems,
  );
};

export const checkInvalidSchedulerMedia = (
  errors: SchedulerMediaValidationError[],
  postType: UniversalPostType,
) => {
  return errors
    .filter((error) => error.postType === postType)
    .some((error) => mediaTypeAndCountErrors.has(error.error));
};

export const getMediaTypeAndCountErrors = (
  errors: SchedulerMediaValidationError[],
): SchedulerMediaValidationError[] => {
  return errors.filter(({ error }) => mediaTypeAndCountErrors.has(error));
};

export const getMediaFormattingErrors = (
  errors: SchedulerMediaValidationError[],
): SchedulerMediaValidationError[] => {
  return errors.filter(({ error }) => mediaFormattingErrors.has(error));
};

export const schedulerMediaValidationErrorsSorter = (
  a: SchedulerMediaValidationError,
  b: SchedulerMediaValidationError,
  selectedMedia: Cuid[],
): number => {
  const getMinPosition = (error: SchedulerMediaValidationError) =>
    Math.min(
      ...error.cuids
        .map((cuid) => selectedMedia.indexOf(cuid))
        .filter((position) => position !== -1),
    );

  return getMinPosition(a) - getMinPosition(b);
};

export const getMediaFormattingErrorsByNetwork = (
  errors: SchedulerMediaValidationError[],
): SchedulerMediaValidationErrorsByNetwork => {
  const result: SchedulerMediaValidationErrorsByNetwork = {
    [SocialNetwork.Facebook]: [],
    [SocialNetwork.TikTok]: [],
    [SocialNetwork.Pinterest]: [],
    [SocialNetwork.Twitter]: [],
    [SocialNetwork.Instagram]: [],
    [SocialNetwork.Snapchat]: [],
    [SocialNetwork.Tumblr]: [],
    [SocialNetwork.YouTube]: [],
    [SocialNetwork.Unknown]: [],
  };

  errors.forEach((error) => {
    result[getSocialNetworkFromPostType(error.postType)].push(error);
  });

  return result;
};

const validateFacebookPageFeedPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  const videoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isVideo(mediaItem.networkItem),
  );
  const nonVideoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && !isVideo(mediaItem.networkItem),
  );
  const hasVideo = videoMediaItems.length > 0;
  const hasImage = nonVideoMediaItems.length > 0;

  if (hasVideo && hasImage) {
    mediaValidationErrors.push({
      cuids: mediaItems.map((mediaItem) => mediaItem.cuid),
      postType: UniversalPostType.FacebookPageFeedPost,
      error: SchedulerMediaError.InvalidMixedMediaTypes,
    });
  }

  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      videoMediaItems,
      UniversalPostType.FacebookPageFeedPost,
      1,
    ),
  );

  if (hasVideo) {
    const cuidsWithTooLongVideo = videoMediaItems
      .filter(
        (mediaItem) =>
          (getMediaMetadata(mediaItem)?.durationMillis ?? 0) >
          FACEBOOK_MAX_VIDEO_DURATION_MILLIS,
      )
      .map((mediaItem) => mediaItem.cuid);

    if (cuidsWithTooLongVideo.length > 0) {
      mediaValidationErrors.push({
        cuids: cuidsWithTooLongVideo,
        postType: UniversalPostType.FacebookPageFeedPost,
        error: SchedulerMediaError.VideoTooLong,
      });
    }
  }

  const cuidsWithTooLargeImage = nonVideoMediaItems
    .filter(
      (mediaItem) =>
        getMediaFileSize(mediaItem) > FACEBOOK_MAX_IMAGE_FILE_SIZE_BYTES,
    )
    .map((mediaItem) => mediaItem.cuid);

  if (cuidsWithTooLargeImage.length > 0) {
    mediaValidationErrors.push({
      cuids: cuidsWithTooLargeImage,
      postType: UniversalPostType.FacebookPageFeedPost,
      error: SchedulerMediaError.ImageFileTooLarge,
    });
  }

  return mediaValidationErrors;
};

const validateTikTokVideoAutomaticPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const videoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isVideo(mediaItem.networkItem),
  );

  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.TikTokVideoAutomaticPost,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  if (videoMediaItems.length !== mediaItems.length) {
    mediaValidationErrors.push({
      cuids: mediaItems
        .filter(
          (mediaItem) =>
            isDefinedWithNetworkItem(mediaItem) &&
            !isVideo(mediaItem.networkItem),
        )
        .map((mediaItem) => mediaItem.cuid),
      postType: UniversalPostType.TikTokVideoAutomaticPost,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      UniversalPostType.TikTokVideoAutomaticPost,
      1,
    ),
  );

  const videoTooShortCuids: Cuid[] = [];
  const videoTooLongCuids: Cuid[] = [];
  const mediaTooSmallCuids: Cuid[] = [];

  videoMediaItems.forEach((videoMediaItem) => {
    const mediaMetadata = getMediaMetadata(videoMediaItem);

    if (
      (mediaMetadata?.durationMillis ?? TIKTOK_MIN_VIDEO_DURATION_MILLIS) <
      TIKTOK_MIN_VIDEO_DURATION_MILLIS
    ) {
      videoTooShortCuids.push(videoMediaItem.cuid);
    }

    if (
      (mediaMetadata?.durationMillis ?? 0) > TIKTOK_MAX_VIDEO_DURATION_MILLIS
    ) {
      videoTooLongCuids.push(videoMediaItem.cuid);
    }

    if (
      (isDefined(mediaMetadata?.width) &&
        mediaMetadata.width < TIKTOK_MIN_DIMENSION_PX) ||
      (isDefined(mediaMetadata?.height) &&
        mediaMetadata.height < TIKTOK_MIN_DIMENSION_PX)
    ) {
      mediaTooSmallCuids.push(videoMediaItem.cuid);
    }
  });

  if (videoTooShortCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooShortCuids,
      postType: UniversalPostType.TikTokVideoAutomaticPost,
      error: SchedulerMediaError.VideoTooShort,
    });
  }

  if (videoTooLongCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLongCuids,
      postType: UniversalPostType.TikTokVideoAutomaticPost,
      error: SchedulerMediaError.VideoTooLong,
    });
  }

  if (mediaTooSmallCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: mediaTooSmallCuids,
      postType: UniversalPostType.TikTokVideoAutomaticPost,
      error: SchedulerMediaError.MediaDimensionsTooSmall,
    });
  }

  return mediaValidationErrors;
};

const validatePinterestOrganicPinMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.PinterestOrganicPin,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  const videoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isVideo(mediaItem.networkItem),
  );
  const imageMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) &&
      isImage(mediaItem.networkItem) &&
      !isGif(mediaItem.networkItem),
  );
  const gifMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isGif(mediaItem.networkItem),
  );

  if (
    (videoMediaItems.length > 0 ? 1 : 0) +
      (imageMediaItems.length > 0 ? 1 : 0) +
      (gifMediaItems.length > 0 ? 1 : 0) >
    1
  ) {
    mediaValidationErrors.push({
      cuids: mediaItems.map((mediaItem) => mediaItem.cuid),
      postType: UniversalPostType.PinterestOrganicPin,
      error: SchedulerMediaError.InvalidMixedMediaTypes,
    });
  }

  mediaValidationErrors = mediaValidationErrors.concat([
    ...checkTooManyMediaError(
      videoMediaItems,
      UniversalPostType.PinterestOrganicPin,
      1,
    ),
    ...checkTooManyMediaError(
      gifMediaItems,
      UniversalPostType.PinterestOrganicPin,
      1,
    ),
    ...checkTooManyMediaError(
      imageMediaItems,
      UniversalPostType.PinterestOrganicPin,
      MAX_CAROUSEL_PIN_IMAGES,
    ),
  ]);

  const { invalidCuids, validationErrors } = validatePinMediaSpecs(mediaItems);

  if (PojoSet.size(invalidCuids) > 0) {
    const cuids = PojoSet.toArray(invalidCuids);
    mediaValidationErrors = mediaValidationErrors.concat(
      PojoSet.toArray(validationErrors).map((validationError) => ({
        cuids,
        postType: UniversalPostType.PinterestOrganicPin,
        error:
          validationError === PinterestPostDetailsValidationError.MediaTooSmall
            ? SchedulerMediaError.MediaDimensionsTooSmall
            : SchedulerMediaError.VideoTooLong,
      })),
    );
  }

  return mediaValidationErrors;
};

const validateTwitterMediaPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  const videoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isVideo(mediaItem.networkItem),
  );
  const imageMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) &&
      isImage(mediaItem.networkItem) &&
      !isGif(mediaItem.networkItem),
  );
  const gifMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isGif(mediaItem.networkItem),
  );

  if (
    (videoMediaItems.length > 0 ? 1 : 0) +
      (imageMediaItems.length > 0 ? 1 : 0) +
      (gifMediaItems.length > 0 ? 1 : 0) >
    1
  ) {
    mediaValidationErrors.push({
      cuids: mediaItems.map((mediaItem) => mediaItem.cuid),
      postType: UniversalPostType.TwitterMediaPost,
      error: SchedulerMediaError.InvalidMixedMediaTypes,
    });
  }

  mediaValidationErrors = mediaValidationErrors.concat([
    ...checkTooManyMediaError(
      videoMediaItems,
      UniversalPostType.TwitterMediaPost,
      1,
    ),
    ...checkTooManyMediaError(
      gifMediaItems,
      UniversalPostType.TwitterMediaPost,
      1,
    ),
    ...checkTooManyMediaError(
      imageMediaItems,
      UniversalPostType.TwitterMediaPost,
      MAX_TWITTER_GALLERY_IMAGE_COUNT,
    ),
  ]);

  const mediaTooSmallCuids: Cuid[] = [];
  const mediaTooLargeCuids: Cuid[] = [];

  mediaItems.forEach((mediaItem) => {
    const mediaMetadata = getMediaMetadata(mediaItem);
    if (!isDefined(mediaMetadata)) {
      return;
    }

    if (isTwitterMediaTooSmall(mediaMetadata)) {
      mediaTooSmallCuids.push(mediaItem.cuid);
    }

    if (isTwitterMediaTooLarge(mediaMetadata)) {
      mediaTooLargeCuids.push(mediaItem.cuid);
    }
  });

  if (mediaTooSmallCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: mediaTooSmallCuids,
      postType: UniversalPostType.TwitterMediaPost,
      error: SchedulerMediaError.MediaDimensionsTooSmall,
    });
  }

  if (mediaTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: mediaTooLargeCuids,
      postType: UniversalPostType.TwitterMediaPost,
      error: SchedulerMediaError.MediaDimensionsTooLarge,
    });
  }

  return mediaValidationErrors;
};

const validateInstagramFeedAutomaticPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.InstagramFeedAutomaticPost,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];

  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      UniversalPostType.InstagramFeedAutomaticPost,
      MAX_IG_GALLERY_POST_MEDIA_COUNT,
    ),
  );

  const invalidMediaTypeCuids: Cuid[] = [];
  const invalidVideoAspectRatioCuids: Cuid[] = [];
  const invalidImageAspectRatioCuids: Cuid[] = [];
  const videoTooShortCuids: Cuid[] = [];
  const videoTooLongCuids: Cuid[] = [];
  const videoTooLargeCuids: Cuid[] = [];
  const imageTooLargeCuids: Cuid[] = [];

  mediaItems.forEach((mediaItem) => {
    if (!isDefinedWithNetworkItem(mediaItem)) {
      return;
    }

    const networkItem = mediaItem.networkItem;

    if (isGif(networkItem)) {
      invalidMediaTypeCuids.push(mediaItem.cuid);
    }

    const mediaMetadata = getMediaMetadata(mediaItem);
    const aspectRatio = getMediaAspectRatio(
      mediaMetadata?.width,
      mediaMetadata?.height,
    );

    if (isVideo(networkItem) && !isValidIgVideoAspectRatio(aspectRatio)) {
      invalidVideoAspectRatioCuids.push(mediaItem.cuid);
    }
    if (!isVideo(networkItem) && !isValidIgImageAspectRatio(aspectRatio)) {
      invalidImageAspectRatioCuids.push(mediaItem.cuid);
    }

    const videoDurationMillis = mediaMetadata?.durationMillis;

    if (
      (videoDurationMillis ?? INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS) <
      INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS
    ) {
      videoTooShortCuids.push(mediaItem.cuid);
    }

    if ((videoDurationMillis ?? 0) > INSTAGRAM_FEED_MAX_VIDEO_DURATION_MILLIS) {
      videoTooLongCuids.push(mediaItem.cuid);
    }

    const fileSizeBytes = getMediaFileSize(mediaItem);
    if (
      isVideo(networkItem) &&
      fileSizeBytes > INSTAGRAM_FEED_MAX_VIDEO_SIZE_BYTES
    ) {
      videoTooLargeCuids.push(mediaItem.cuid);
    }

    if (
      isImage(networkItem) &&
      fileSizeBytes > INSTAGRAM_FEED_MAX_IMAGE_SIZE_BYTES
    ) {
      imageTooLargeCuids.push(mediaItem.cuid);
    }
  });

  if (invalidMediaTypeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidMediaTypeCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  if (invalidVideoAspectRatioCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidVideoAspectRatioCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.InvalidVideoAspectRatio,
    });
  }

  if (invalidImageAspectRatioCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidImageAspectRatioCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.InvalidImageAspectRatio,
    });
  }

  if (videoTooShortCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooShortCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.VideoTooShort,
    });
  }

  if (videoTooLongCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLongCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.VideoTooLong,
    });
  }

  if (videoTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLargeCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.VideoFileTooLarge,
    });
  }

  if (imageTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: imageTooLargeCuids,
      postType: UniversalPostType.InstagramFeedAutomaticPost,
      error: SchedulerMediaError.ImageFileTooLarge,
    });
  }

  return mediaValidationErrors;
};

// We don't validate anything for manual feed posts media
const validateInstagramFeedManualPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  return [];
};

const validateInstagramReelsAutomaticPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.InstagramReelsAutomaticPost,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      UniversalPostType.InstagramReelsAutomaticPost,
      1,
    ),
  );

  const invalidMediaTypeCuids: Cuid[] = [];
  const invalidAspectRatioCuids: Cuid[] = [];
  const videoTooShortCuids: Cuid[] = [];
  const videoTooLongCuids: Cuid[] = [];
  const mediaTooLargeCuids: Cuid[] = [];

  mediaItems.forEach((mediaItem) => {
    if (!isDefinedWithNetworkItem(mediaItem)) {
      return;
    }

    const networkItem = mediaItem.networkItem;

    if (!isVideo(networkItem)) {
      invalidMediaTypeCuids.push(mediaItem.cuid);
    }

    const mediaMetadata = getMediaMetadata(mediaItem);
    const aspectRatio = getMediaAspectRatio(
      mediaMetadata?.width,
      mediaMetadata?.height,
    );

    if (!isValidIgReelsAspectRatio(aspectRatio)) {
      invalidAspectRatioCuids.push(mediaItem.cuid);
    }

    const videoDurationMillis = mediaMetadata?.durationMillis;
    if (
      (videoDurationMillis ?? REELS_MIN_VIDEO_DURATION_MILLIS) <
      REELS_MIN_VIDEO_DURATION_MILLIS
    ) {
      videoTooShortCuids.push(mediaItem.cuid);
    }

    if ((videoDurationMillis ?? 0) > REELS_MAX_VIDEO_DURATION_MILLIS) {
      videoTooLongCuids.push(mediaItem.cuid);
    }

    const videoSize = getMediaFileSize(mediaItem);
    if (videoSize > REELS_MAX_VIDEO_SIZE) {
      mediaTooLargeCuids.push(mediaItem.cuid);
    }
  });

  if (invalidMediaTypeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidMediaTypeCuids,
      postType: UniversalPostType.InstagramReelsAutomaticPost,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  if (invalidAspectRatioCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidAspectRatioCuids,
      postType: UniversalPostType.InstagramReelsAutomaticPost,
      error: SchedulerMediaError.InvalidVideoAspectRatio,
    });
  }

  if (videoTooShortCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooShortCuids,
      postType: UniversalPostType.InstagramReelsAutomaticPost,
      error: SchedulerMediaError.VideoTooShort,
    });
  }

  if (videoTooLongCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLongCuids,
      postType: UniversalPostType.InstagramReelsAutomaticPost,
      error: SchedulerMediaError.VideoTooLong,
    });
  }

  if (mediaTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: mediaTooLargeCuids,
      postType: UniversalPostType.InstagramReelsAutomaticPost,
      error: SchedulerMediaError.VideoFileTooLarge,
    });
  }

  return mediaValidationErrors;
};

const validateInstagramReelsManualPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.InstagramReelsManualPost,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      UniversalPostType.InstagramReelsManualPost,
      1,
    ),
  );

  const invalidMediaTypeCuids: Cuid[] = [];

  mediaItems.forEach((mediaItem) => {
    if (!isDefinedWithNetworkItem(mediaItem)) {
      return;
    }

    const networkItem = mediaItem.networkItem;

    if (!isVideo(networkItem)) {
      invalidMediaTypeCuids.push(mediaItem.cuid);
    }
  });

  if (invalidMediaTypeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidMediaTypeCuids,
      postType: UniversalPostType.InstagramReelsManualPost,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  return mediaValidationErrors;
};

const validateInstagramStoryGroupAutomaticPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  return validateInstagramStoryMedia(
    mediaItems,
    UniversalPostType.InstagramStoryGroupAutomaticPost,
  );
};

const validateInstagramStoryGroupPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  return validateInstagramStoryMedia(
    mediaItems,
    UniversalPostType.InstagramStoryGroupPost,
  );
};

const validateInstagramStoryMedia = (
  mediaItems: IMlsMediaItem[],
  postType: UniversalPostType,
): SchedulerMediaValidationError[] => {
  const noMediaErrors = checkNoMediaError(mediaItems, postType);
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];

  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      postType,
      MAX_IG_GALLERY_POST_MEDIA_COUNT,
    ),
  );

  const invalidMediaTypeCuids: Cuid[] = [];
  const videoTooShortCuids: Cuid[] = [];
  const videoTooLongCuids: Cuid[] = [];
  const videoTooLargeCuids: Cuid[] = [];
  const imageTooLargeCuids: Cuid[] = [];

  mediaItems.forEach((mediaItem) => {
    const mediaMetadata = getMediaMetadata(mediaItem);
    if (!isDefinedWithNetworkItem(mediaItem)) {
      return;
    }

    const networkItem = mediaItem.networkItem;
    if (isGif(networkItem)) {
      invalidMediaTypeCuids.push(mediaItem.cuid);
    }

    if (isVideo(networkItem)) {
      const videoDurationMillis = mediaMetadata?.durationMillis;
      const videoSizeBytes = getMediaFileSize(mediaItem);

      if (
        (videoDurationMillis ?? STORY_MIN_VIDEO_DURATION_MILLIS) <
        STORY_MIN_VIDEO_DURATION_MILLIS
      ) {
        videoTooShortCuids.push(mediaItem.cuid);
      }

      if ((videoDurationMillis ?? 0) > STORY_MAX_VIDEO_DURATION_MILLIS) {
        videoTooLongCuids.push(mediaItem.cuid);
      }

      if ((videoSizeBytes ?? 0) > STORY_MAX_VIDEO_SIZE) {
        videoTooLargeCuids.push(mediaItem.cuid);
      }
    }

    if (isImage(networkItem) && !isGif(mediaItem.networkItem)) {
      const imageSizeBytes = getMediaFileSize(mediaItem);

      if (imageSizeBytes > STORY_MAX_IMAGE_SIZE) {
        imageTooLargeCuids.push(mediaItem.cuid);
      }
    }
  });

  if (invalidMediaTypeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: invalidMediaTypeCuids,
      postType,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  if (videoTooShortCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooShortCuids,
      postType,
      error: SchedulerMediaError.VideoTooShort,
    });
  }

  if (videoTooLongCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLongCuids,
      postType,
      error: SchedulerMediaError.VideoTooLong,
    });
  }

  if (videoTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLargeCuids,
      postType,
      error: SchedulerMediaError.VideoFileTooLarge,
    });
  }

  if (imageTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: imageTooLargeCuids,
      postType,
      error: SchedulerMediaError.ImageFileTooLarge,
    });
  }

  return mediaValidationErrors;
};

const validateYouTubeAutomaticPostMedia = (
  mediaItems: IMlsMediaItem[],
): SchedulerMediaValidationError[] => {
  const videoMediaItems = mediaItems.filter(
    (mediaItem) =>
      isDefinedWithNetworkItem(mediaItem) && isVideo(mediaItem.networkItem),
  );

  const noMediaErrors = checkNoMediaError(
    mediaItems,
    UniversalPostType.YouTubeAutomaticPost,
  );
  if (noMediaErrors.length > 0) {
    return noMediaErrors;
  }

  let mediaValidationErrors: SchedulerMediaValidationError[] = [];
  if (videoMediaItems.length !== mediaItems.length) {
    mediaValidationErrors.push({
      cuids: mediaItems
        .filter(
          (mediaItem) =>
            isDefinedWithNetworkItem(mediaItem) &&
            !isVideo(mediaItem.networkItem),
        )
        .map((mediaItem) => mediaItem.cuid),
      postType: UniversalPostType.YouTubeAutomaticPost,
      error: SchedulerMediaError.InvalidMediaType,
    });
  }

  mediaValidationErrors = mediaValidationErrors.concat(
    checkTooManyMediaError(
      mediaItems,
      UniversalPostType.YouTubeAutomaticPost,
      1,
    ),
  );

  const videoTooLargeCuids: Cuid[] = [];
  const videoTooLongCuids: Cuid[] = [];

  videoMediaItems.forEach((videoMediaItem) => {
    const mediaMetadata = getMediaMetadata(videoMediaItem);
    const fileSizeBytes = getMediaFileSize(videoMediaItem);
    const networkItem = videoMediaItem.networkItem;

    if (
      networkItem &&
      isVideo(networkItem) &&
      fileSizeBytes > YOUTUBE_MAX_VIDEO_SIZE_BYTES
    ) {
      videoTooLargeCuids.push(videoMediaItem.cuid);
    }

    if (
      (mediaMetadata?.durationMillis ?? 0) > YOUTUBE_MAX_VIDEO_DURATION_MILLIS
    ) {
      videoTooLongCuids.push(videoMediaItem.cuid);
    }
  });

  if (videoTooLargeCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLargeCuids,
      postType: UniversalPostType.YouTubeAutomaticPost,
      error: SchedulerMediaError.VideoFileTooLarge,
    });
  }

  if (videoTooLongCuids.length > 0) {
    mediaValidationErrors.push({
      cuids: videoTooLongCuids,
      postType: UniversalPostType.YouTubeAutomaticPost,
      error: SchedulerMediaError.VideoTooLong,
    });
  }

  return mediaValidationErrors;
};

const checkNoMediaError = (
  mediaItems: IMlsMediaItem[],
  postType: UniversalPostType,
): SchedulerMediaValidationError[] => {
  if (mediaItems.length === 0) {
    return [
      {
        cuids: [],
        postType,
        error: SchedulerMediaError.TooFewMediaItems,
      },
    ];
  }

  return [];
};

const checkTooManyMediaError = (
  mediaItems: IMlsMediaItem[],
  postType: UniversalPostType,
  maxMediaCount: number,
): SchedulerMediaValidationError[] => {
  if (mediaItems.length > maxMediaCount) {
    return [
      {
        cuids: mediaItems.map((mediaItem) => mediaItem.cuid),
        postType,
        error: SchedulerMediaError.TooManyMediaItems,
      },
    ];
  }

  return [];
};
